import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  GraduationCap, Users, Plus, Search, MoreHorizontal, 
  ArrowLeft, Loader2, AlertCircle, Pencil, Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';

import { API_URL } from '../utils/api';

export function StudentsPage() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', grade: '', school_id: '', 
    date_of_birth: '', disability_category: '', state_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, schoolsRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`, { withCredentials: true }),
        axios.get(`${API_URL}/api/schools`, { withCredentials: true })
      ]);
      setStudents(studentsRes.data);
      setSchools(schoolsRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/students`, formData, { withCredentials: true });
      setCreateOpen(false);
      setFormData({ first_name: '', last_name: '', grade: '', school_id: '', date_of_birth: '', disability_category: '', state_id: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create student');
    } finally {
      setSubmitting(false);
    }
  };

  const getSchoolName = (schoolId) => schools.find(s => s.school_id === schoolId)?.name || '';

  const filteredStudents = students.filter(s =>
    `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canManage = ['admin', 'case_manager', 'teacher', 'therapist'].includes(user?.role);

  const grades = ['Pre-K', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const disabilityCategories = [
    'Autism', 'Deaf-Blindness', 'Deafness', 'Developmental Delay', 
    'Emotional Disturbance', 'Hearing Impairment', 'Intellectual Disability',
    'Multiple Disabilities', 'Orthopedic Impairment', 'Other Health Impairment',
    'Specific Learning Disability', 'Speech or Language Impairment',
    'Traumatic Brain Injury', 'Visual Impairment'
  ];

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-[#F3F4F1] rounded-lg" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-[#595959]" />
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-7 w-7 text-[#2C4C3B]" />
              <span className="font-outfit text-xl font-semibold text-[#1C1C1C]">FrameEd</span>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-[#1C1C1C] mb-1">Students</h1>
            <p className="text-[#595959]">Shared student registry across all FrameEd products</p>
          </div>
          {canManage && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl" data-testid="create-student-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-outfit">Add Student</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        placeholder="First name"
                        className="rounded-xl"
                        required
                        data-testid="student-firstname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        placeholder="Last name"
                        className="rounded-xl"
                        required
                        data-testid="student-lastname-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Grade</Label>
                      <Select value={formData.grade} onValueChange={(v) => setFormData({...formData, grade: v})}>
                        <SelectTrigger className="rounded-xl" data-testid="student-grade-select">
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent>
                          {grades.map((g) => (
                            <SelectItem key={g} value={g}>{g}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>School</Label>
                    <Select value={formData.school_id} onValueChange={(v) => setFormData({...formData, school_id: v})}>
                      <SelectTrigger className="rounded-xl" data-testid="student-school-select">
                        <SelectValue placeholder="Select school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.map((s) => (
                          <SelectItem key={s.school_id} value={s.school_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Disability Category</Label>
                    <Select value={formData.disability_category} onValueChange={(v) => setFormData({...formData, disability_category: v})}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {disabilityCategories.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>State ID</Label>
                    <Input
                      value={formData.state_id}
                      onChange={(e) => setFormData({...formData, state_id: e.target.value})}
                      placeholder="State student ID"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl" data-testid="submit-student-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Add Student
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">×</button>
          </div>
        )}

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
          <Input
            placeholder="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl bg-white"
            data-testid="search-students-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-2xl">
            <Users className="h-12 w-12 mx-auto text-[#595959] mb-4" />
            <h3 className="font-outfit text-lg font-medium text-[#1C1C1C] mb-2">No students yet</h3>
            <p className="text-[#595959]">Add students to the shared registry</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredStudents.map((student) => (
              <div
                key={student.student_id}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow"
                data-testid={`student-card-${student.student_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#C2D3C0]/30 flex items-center justify-center">
                      <span className="font-outfit font-semibold text-[#3A5A3A]">
                        {student.first_name[0]}{student.last_name[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C]">
                        {student.first_name} {student.last_name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {student.grade && (
                          <Badge className="bg-[#A7C7E7]/20 text-[#3B6A9B]">Grade {student.grade}</Badge>
                        )}
                        {student.disability_category && (
                          <Badge className="bg-[#E2DACD]/50 text-[#6B5D4D]">{student.disability_category}</Badge>
                        )}
                      </div>
                      {student.school_id && (
                        <p className="text-sm text-[#595959] mt-2">{getSchoolName(student.school_id)}</p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Eye className="h-4 w-4 mr-2" />
                        View Profile
                      </DropdownMenuItem>
                      {canManage && (
                        <DropdownMenuItem>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
